<template>
	<div
		v-if="active"
		v-click-outside="onClickOutside"
		class="personification"
	>
		<helio-field
			:placeholder="$t('header.personification.placeholder')"
			:display-success="false"
			:autofocus="true"
			:icon="loading ? 'spinner-third' : 'search'"
			:icon-spin="loading"
			icon-weight="light"
			icon-position="left"
			class="personification__field"
			data-anchor="personification"
			@changed="debouncedGetUsers"
		/>

		<helio-icon
			token="times"
			class="personification__close"
			@click="onClickOutside"
		/>

		<div
			v-if="users && term && term.length >= 3 && loading"
			class="personification__dropdown"
		>
			<helio-personification-skeleton />
		</div>

		<div
			v-if="users && term && term.length >= 3 && (!loading || users.length > 0)"
			class="personification__dropdown scroll scroll--small"
		>
			<div
				v-if="users.length > 0"
				class="personification__users-wrapper"
			>
				<template
					v-for="(singleUser, index) in users"
				>
					<helio-personifiable-user
						:key="singleUser.uuid"
						:user="singleUser"
						:disabled="singleUser.uuid === null"
						:class="[
							`personifiable-user--${index}`,
							{
								'personifiable-user--focused': index === currentIndex,
							}
						]"
						@mouseover.native="currentIndex = null"
						@userSelected="getSingleUser"
					/>
				</template>
			</div>
			<div
				v-if="users.length === 0 && term.length > 2"
				class="personification__dropdown--empty"
			>
				<span class="personification__dropdown--empty-image" />
				<p class="personification__dropdown--empty-text">
					{{ $t('header.personification.no-users', [term]) }}
				</p>
			</div>
		</div>
	</div>
</template>

<script>
import _throttle from 'lodash/throttle';
import axios from 'axios';
import Field from '../../field/Field.vue';
import PersonifiableUser from './personifiable-user/PersonifiableUser.vue';
import PersonificationSkeleton from './personification-skeleton/PersonificationSkeleton.vue';
import Icon from '../../icon/Icon.vue';

export default {
	components: {
		'helio-field': Field,
		'helio-personifiable-user': PersonifiableUser,
		'helio-icon': Icon,
		'helio-personification-skeleton': PersonificationSkeleton,
	},

	props: {
		active: {
			type: Boolean,
			required: true,
		},
	},

	data() {
		return {
			isMobile: false,
			users: null,
			user: null,
			currentIndex: null,
			term: null,
			cancelSource: null,
			loading: false,
		};
	},

	watch: {
		currentIndex() {
			this.$scrollTo(`.personifiable-user--${this.currentIndex}`, 200, {
				container: '.personification__dropdown',
				offset: -10,
			});
		},
	},

	mounted() {
		window.addEventListener('resize', this.onResize);
		window.addEventListener('keydown', this.checkKeyDown);

		this.createCancelSource();
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.onResize);
		window.removeEventListener('keydown', this.checkKeyDown);
	},

	methods: {
		/* eslint-disable-next-line */
		debouncedGetUsers: _throttle(function (term) {
			this.term = term;
			this.getUsers(term);
		}, 750, { leading: true, trailing: true }),

		onResize() {
			this.isMobile = window.innerWidth < 720;
		},

		createCancelSource() {
			const { CancelToken } = axios;
			this.cancelSource = CancelToken.source();
		},

		getUsers(term) {
			if (term && term !== '' && term.length >= 3) {
				const language = this.$i18n.locale;
				const endpoint = `/users?term=${term}&language=${language}`;

				if (this.cancelSource) {
					this.cancelSource.cancel('One search at the time');
					this.createCancelSource();
				}

				this.loading = true;

				window.Centios
					.core('GET', endpoint, { cancelToken: this.cancelSource.token })
					.then((response) => {
						const { data } = response;

						this.loading = false;

						if (data.code && [404, 403, 422, 401].includes(data.code)) {
							this.$emit('api-error');
						} else if (data.code && data.code === 200 && data.data.users) {
							this.users = data.data.users;
						}
					}).catch(() => {
						// Do nothing - it is okay
					});
			} else {
				this.users = [];
			}
		},

		getSingleUser(user) {
			const { uuid } = user;
			const endpoint = `/api/v1/users/${uuid}`;

			window.Centios.sso('GET', endpoint).then((response) => {
				const { data } = response;

				if (data.code && [404, 403, 422, 401].includes(data.code)) {
					this.$emit('api-error');
				} else if (data.code && data.code === 200 && data.data) {
					this.user = data.data;
					this.$emit('userSelected', this.user);
					this.users = null;
					this.currentIndex = null;
					this.$emit('close');
				}
			});
		},

		onClickOutside() {
			this.users = null;
			this.currentIndex = null;
			this.$emit('close');
		},

		checkKeyDown(event) {
			// Only checkKeyDown if it is active
			if (!this.active) {
				return;
			}

			const { keyCode } = event;

			if (!this.users && keyCode === 27) {
				// Esc
				this.onClickOutside();
				return;
			}

			const indexMaximum = this.users ? this.users.length - 1 : 0;

			// Enter
			if (keyCode === 13 && this.currentIndex !== null) {
				const focusedUser = this.users[this.currentIndex];
				this.getSingleUser(focusedUser);
			}

			// Escape
			if (keyCode === 27) {
				this.onClickOutside();
			}

			// Up arrow
			if (keyCode === 38 && this.users && !this.isMobile) {
				event.preventDefault();
				this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : indexMaximum;
			}

			// Down arrow and tab
			if ((keyCode === 40 || keyCode === 9) && this.users && !this.isMobile) {
				event.preventDefault();

				if (this.currentIndex === null || this.currentIndex >= indexMaximum) {
					this.currentIndex = 0;
				} else {
					this.currentIndex += 1;
				}
			}
		},
	},
};
</script>

<style lang="scss" src="./personification.scss" />
